import { useGlobalConfig } from "../hooks/useGlobalConfig";
import { getProductConfig } from "../config/product";
import { isConfigYes } from "../utils/config";

// Display label for the Hub feature. The route/dir/component/code all stay
// "hub" (URL is always /app/hub) — ONLY the visible label is prefixed with the
// customer's alias, e.g. "Helisgi Hub". Alias = the per-build REACT_APP_CUSTOMER
// (the same value sent as X-Install-Name in src/api.ts). Falls back to plain
// "Hub" when the alias is unset (e.g. a generic/dev build).
const alias = (process.env.REACT_APP_CUSTOMER ?? "").trim();

// "Gch" by default, "GCH" for acronym customers; "" when no alias is set.
// Also used for the browser tab title (App.tsx / PageTitle.tsx).
export const displayAlias = (uppercaseAlias: boolean) =>
  uppercaseAlias
    ? alias.toUpperCase()
    : `${alias.charAt(0).toUpperCase()}${alias.slice(1)}`;

// Per-tenant RUNTIME override, resolved from the hostname (src/config/product.ts).
//
// Why this takes precedence over the alias below: `alias` is read from
// REACT_APP_CUSTOMER, which is baked in at BUILD time and pinned to "aeronet" in
// .env.production. One image now serves every tenant, so the build-time value is
// necessarily the same on every host and cannot rename a single tenant's Hub.
// Only tenants that explicitly set `hubLabel` are affected; every other tenant
// falls through to the historic alias path unchanged.
export const hubLabel = (uppercaseAlias: boolean) => {
  const override = getProductConfig().hubLabel;
  if (override) return override;
  return alias ? `${displayAlias(uppercaseAlias)} Hub` : "Hub";
};

// Acronym customers (e.g. GCH) set config_function="global",
// config_variable="global_hub_alias_uppercase" to "yes", which renders the alias
// all-caps ("GCH Hub"). Default off. Mirrored in Roxen
// htdocs/mod_nav/adminnav.inc via modcfg.global.hub_alias_uppercase.
//
// ⚠️ The /v6/config serializer strips the "global_" prefix but does NOT coerce
// yes/no to a boolean. This comment previously claimed it did, and both gates
// below were `=== true` — unsatisfiable, since the value arrives as the STRING
// "yes". Measured on g2: 3316 config rows, ZERO return a boolean. The identical
// mistake in src/help/HelpMenu.tsx hid the Hub menu entry entirely; here it is
// cosmetic (an acronym tenant silently lost its all-caps alias), but it is the
// same defect and it is fixed the same way.
//
// Config values are STRINGS — never coerce, never assume the server did
// (memory reference_config_value_not_coerced).
export const useDisplayAlias = () => {
  const globalConfig = useGlobalConfig();
  return displayAlias(isConfigYes(globalConfig?.hub_alias_uppercase));
};

export const useHubLabel = () => {
  const globalConfig = useGlobalConfig();
  return hubLabel(isConfigYes(globalConfig?.hub_alias_uppercase));
};
